import { WithDeleted } from '@common/typescript/objects/WithDeleted';

import { NTBaseUser } from '@commonTuna/react/objects/NTBaseUser';

export enum UserGroupType {
	LowOnStock = 1,
	InquiryNotify = 2,
	SpecialPurchased = 3,
	CanChangePrice = 4,
	InsuranceAccess = 5,
	Support = 6,
	RefundAccess = 7,
	ChangeOrderTimeAccess = 8,
	FinancesWidgets = 9,
	ProductInvoiceAccess = 11,
	ErxAgent = 12,
	AppointmentHistory = 101,
	NewAppointmentHistory = 102,
	PatientRowInfo = 103,
	Comments = 104,
	RecentPatients = 105,
	AdvancedTimePicking = 106,
	DashboardMenuInWizard = 107,
	SkipNegativeAnswersInWizardPdf = 108,
	WizardPrintAllQuestions = 109,
	FixedCalendarHeight = 110,
	PatientInfoModal = 111,
	ShowChatInModal = 112,
	AppointmentModalWidget = 113,
	PatientMenuGroups = 114,
	SaveFiltersForPages = 115,
	SystemSupport = 1001,
	SystemAllChatsEnable = 1002,
	SystemVerificator = 1003,
	SystemBotTeacher = 1004,
	PortalContentManager = 1005,
	RecipientOfSupportEmailsFromPortal = 1006,
}

export const UserGroupTypeNames = {
	[UserGroupType.LowOnStock]: 'Low stock',
	[UserGroupType.InquiryNotify]: 'Inquiries',
	[UserGroupType.SpecialPurchased]: 'Special purchased',
	[UserGroupType.CanChangePrice]: 'Price change',
	[UserGroupType.InsuranceAccess]: 'Insurance billing',
	[UserGroupType.Support]: 'Patient support chat',
	[UserGroupType.RefundAccess]: 'Refunds',
	[UserGroupType.ChangeOrderTimeAccess]: 'Change order date',
	[UserGroupType.AppointmentHistory]: 'Appointment history',
	[UserGroupType.FinancesWidgets]: 'Finances widgets',
	[UserGroupType.PatientRowInfo]: 'Patient row info',
	[UserGroupType.Comments]: 'Comments',
	[UserGroupType.RecentPatients]: 'Recent patients',
	[UserGroupType.AdvancedTimePicking]: 'Advanced time picking',
	[UserGroupType.DashboardMenuInWizard]: 'Dashboard menu in wizard',
	[UserGroupType.SkipNegativeAnswersInWizardPdf]: 'Skip negative answers in wizard pdf',
	[UserGroupType.WizardPrintAllQuestions]: 'Wizard print all questions',
	[UserGroupType.FixedCalendarHeight]: 'Fixed calendar height',
	[UserGroupType.PatientInfoModal]: 'Patient info modal',
	[UserGroupType.SystemSupport]: 'System support',
	[UserGroupType.SystemAllChatsEnable]: 'All chats enable',
	[UserGroupType.SystemVerificator]: 'Verificator',
	[UserGroupType.SystemBotTeacher]: 'Bot teacher',
	[UserGroupType.PortalContentManager]: 'Portal content manager',
	[UserGroupType.AppointmentModalWidget]: 'Appointment widget',
	[UserGroupType.RecipientOfSupportEmailsFromPortal]: 'Recipient of support emails from portal',
	[UserGroupType.ProductInvoiceAccess]: 'Inventory invoices',
	[UserGroupType.ErxAgent]: 'E-Rx agent',
	[UserGroupType.PatientMenuGroups]: 'Group patient menu',
	[UserGroupType.SaveFiltersForPages]: 'Save filters for pages',
};

export const UserGroupTypeDescription = {
	[UserGroupType.RefundAccess]: 'Access to patient refunds processing',
	[UserGroupType.CanChangePrice]: 'Allows to change price at checkout, add discounts',
	[UserGroupType.ProductInvoiceAccess]: 'Add, delete invoices for products added to the inventory',
	[UserGroupType.ErxAgent]: 'Access to E-Rx functionality, subject to credentialing',
	[UserGroupType.AppointmentHistory]: 'Shows list of patient’s prior appointments, providers, procedures',
	[UserGroupType.AppointmentModalWidget]: 'Shows graph of patient’s prior appointments status, providers, sources, types',
	[UserGroupType.PatientRowInfo]: 'Shows graph of patient’s records (consents, insurance, DLs, questionnaires, etc) statuses:'
	+ ' red = incomplete; green = complete, yellow = partially complete; blue = not required',
	[UserGroupType.PatientInfoModal]: 'Shows additional window with patient’s name, age, allergy, balance, debt, total spend and Prepaid Inventory',
	[UserGroupType.RecentPatients]: 'useful for faster scheduling for a follow-up visit',
	[UserGroupType.AdvancedTimePicking]: '1 day view when unchecked',
	[UserGroupType.SkipNegativeAnswersInWizardPdf]: 'when enabled all questions with answer “NO” will not be included in the medical note',
	[UserGroupType.WizardPrintAllQuestions]: 'when enabled all questions without answers will not be included in the medical note',
	[UserGroupType.Comments]: 'Enable/disable patient comments on Appointment Modal',
	[UserGroupType.PatientMenuGroups]: 'When checked, patient data is organized by category in subfolders, otherwise displayed in one row',
	[UserGroupType.DashboardMenuInWizard]: 'when enabled, the left side menu will be hidden while Patient Visit and Checkout Wizards are in use',
	[UserGroupType.SaveFiltersForPages]: 'Save page filters when you change them',
};

export const UserGroupTypeAccessGroup = [
	UserGroupType.RefundAccess,
	UserGroupType.CanChangePrice,
	UserGroupType.FinancesWidgets,
	UserGroupType.ChangeOrderTimeAccess,
	UserGroupType.InsuranceAccess,
	UserGroupType.ProductInvoiceAccess,
	UserGroupType.ErxAgent,
	UserGroupType.Support,
];

export const UserGroupTypeNotificationsGroup = [
	UserGroupType.InquiryNotify,
	UserGroupType.SpecialPurchased,
	UserGroupType.LowOnStock,
];

export const UserGroupTypeOtherGroup = [
	UserGroupType.DashboardMenuInWizard,
	UserGroupType.FixedCalendarHeight,
	UserGroupType.SystemSupport,
	UserGroupType.SystemAllChatsEnable,
	UserGroupType.SystemVerificator,
	UserGroupType.SystemBotTeacher,
	UserGroupType.PortalContentManager,
	UserGroupType.RecipientOfSupportEmailsFromPortal,
	UserGroupType.PatientMenuGroups,
	UserGroupType.SaveFiltersForPages,
];

export const UserGroupTypeAppointmentGroup = [
	UserGroupType.AppointmentHistory,
	UserGroupType.AppointmentModalWidget,
	UserGroupType.PatientRowInfo,
	UserGroupType.PatientInfoModal,
	UserGroupType.Comments,
	UserGroupType.RecentPatients,
	UserGroupType.AdvancedTimePicking,
];

export const UserGroupTypeMedicalNoteGroup = [
	UserGroupType.SkipNegativeAnswersInWizardPdf,
	UserGroupType.WizardPrintAllQuestions,
];

export interface UserGroup extends WithDeleted {
	userId: number;
	user: NTBaseUser;
	userGroupType: UserGroupType;
}
